"use client";

import Image from "next/image";
import { useIntl } from "react-intl";

import "./CardChevron.css";

/** The one chevron in the app. A single asset (down_arrow.svg, red baked in)
 *  turned per direction — the codebase previously shipped this same glyph as
 *  four duplicate files plus two thinner variants and AntD's outlined icons.
 *
 *  Card rows use the default 15x18; dropdown carets pass a smaller size.
 *
 *  The turn is a `data-direction` hook resolved by CardChevron.css, NOT an
 *  inline transform: an inline transform wins over a consumer's class and would
 *  silently drop positioning like `.dropdown-arrow { transform: translateY(-50%) }`.
 *  The CSS `rotate` longhand also composes with a consumer's `transform` rather
 *  than replacing it, so a caller can still translate the chevron freely. */
type Props = {
  direction: "right" | "up" | "down";
  className?: string;
  width?: number;
  height?: number;
};

export default function CardChevron({
  direction,
  className,
  width = 15,
  height = 18,
}: Props) {
  const intl = useIntl();
  // Only the horizontal one mirrors in RTL — flipping up/down inverts meaning.
  const isMirrored = direction === "right" && intl.locale === "ar";

  return (
    <Image
      src="/assets/booking/down_arrow.svg"
      className={className ? `card-chevron ${className}` : "card-chevron"}
      width={width}
      height={height}
      alt=""
      aria-hidden
      data-direction={isMirrored ? "right-rtl" : direction}
    />
  );
}
